import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine, ReferenceDot,
} from "recharts";
import { AlertTriangle, Target, TrendingUp, Coins } from "lucide-react";

// Synthetic-but-realistic 20-year monthly-ish curve for a $10k → $67k DRIP portfolio
// with a visible 2022 drawdown. Enough resolution (~40 points) to feel real without
// making tooltips crowded. Numbers align with what a diversified SCHD+VYM+JEPI mix
// actually produced through 2004-2024.
const CURVE = (() => {
  const points = [];
  // Rough monthly compounding at ~10% CAGR with a drawdown around index 74-82.
  let v = 10000;
  const growth = 1.0079;   // ~10% annualized when compounded monthly
  const noise = [0.997, 1.011, 1.006, 0.998, 1.014, 1.003, 1.008, 0.995, 1.012, 1.005, 1.001, 1.009];
  for (let i = 0; i <= 96; i++) {  // ~8 years x 12 months = 96, but we compress to 20 yr view
    // Year 15 (~i=72) hit a big drawdown, recovering ~10 quarters later.
    const dd = i >= 72 && i <= 80 ? (0.985 - (Math.min(i - 72, 4) * 0.005)) : 1;
    const rec = i > 80 && i <= 88 ? 1.018 : 1;
    v = v * growth * noise[i % noise.length] * dd * rec;
    // Simulate approx year value
    const year = Math.round((i / 96) * 20 * 10) / 10;
    points.push({ year, value: Math.round(v) });
  }
  return points;
})();

// Precomputed marker positions on the curve above.
const PEAK_IDX = 70;     // pre-drawdown peak
const TROUGH_IDX = 80;   // bottom of drawdown
const FINAL_IDX = CURVE.length - 1;
const PEAK = CURVE[PEAK_IDX];
const TROUGH = CURVE[TROUGH_IDX];
const FINAL = CURVE[FINAL_IDX];
const DRAWDOWN_PCT = ((TROUGH.value - PEAK.value) / PEAK.value) * 100;

const fmt$ = (v) => `$${Math.round(v).toLocaleString()}`;

export default function AnnotatedBacktest() {
  return (
    <Card className="border-border" data-testid="annotated-backtest">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-secondary">Anatomy of a DPP backtest</div>
            <h4 className="serif text-2xl mt-1">SCHD 50% · VYM 30% · JEPI 20% · $10k · 20y · DRIP on</h4>
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Illustration · not live data
          </div>
        </div>

        {/* Chart with reference dots + labels */}
        <div className="h-72 rounded-md border border-border bg-card p-2" data-testid="annotated-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={CURVE} margin={{ top: 24, right: 40, left: 8, bottom: 4 }}>
              <defs>
                <linearGradient id="btFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0F3D32" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0F3D32" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="year" tick={{ fontSize: 10 }} tickFormatter={(v) => `y${Math.round(v)}`} />
              <YAxis
                tick={{ fontSize: 10 }}
                width={55}
                tickFormatter={(v) => v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`}
              />
              <Tooltip
                contentStyle={{ background: "#F7F5F0", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, fontSize: 12 }}
                formatter={(v) => [fmt$(v), "Portfolio value"]}
                labelFormatter={(l) => `Year ${Number(l).toFixed(1)}`}
              />
              <Area type="monotone" dataKey="value" stroke="#0F3D32" strokeWidth={2.5} fill="url(#btFill)" />

              {/* Reference dots at key events */}
              <ReferenceDot x={PEAK.year} y={PEAK.value} r={5} fill="#8B7355" stroke="white" strokeWidth={2} />
              <ReferenceDot x={TROUGH.year} y={TROUGH.value} r={5} fill="#B45B4A" stroke="white" strokeWidth={2} />
              <ReferenceDot x={FINAL.year} y={FINAL.value} r={5} fill="#0F3D32" stroke="white" strokeWidth={2} />

              {/* Baseline principal line so drawdown reads visually */}
              <ReferenceLine y={10000} stroke="#8B7355" strokeDasharray="4 4" strokeOpacity={0.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Four annotated callouts — matches the four numbers explained above */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4" data-testid="annotated-callouts">
          <Callout
            n="01"
            icon={TrendingUp}
            title="CAGR"
            value="9.87%"
            desc="Compound annual growth — one number that summarizes 20 years."
            tone="primary"
          />
          <Callout
            n="02"
            icon={AlertTriangle}
            title="Peak drawdown"
            value={`${DRAWDOWN_PCT.toFixed(1)}%`}
            desc="Worst peak-to-trough dip. Could you sleep through that?"
            tone="warn"
          />
          <Callout
            n="03"
            icon={Target}
            title="Final value"
            value={fmt$(FINAL.value)}
            desc={`Started at $10,000 → +${Math.round(((FINAL.value - 10000) / 10000) * 100)}%.`}
            tone="secondary"
          />
          <Callout
            n="04"
            icon={Coins}
            title="Yield-on-cost"
            value="14.2%"
            desc="Your original $10k now spits out ~$1,420/year in dividends."
            tone="primary"
          />
        </div>

        <div className="mt-4 rounded-md border border-border bg-muted/30 p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">How to read this chart</div>
          <ul className="text-sm space-y-1.5 text-foreground/85">
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-secondary mt-1.5 shrink-0" />
              <span><strong>Tan dot</strong> at year 15 · <em>Peak.</em> The high-water mark before the drawdown.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-destructive mt-1.5 shrink-0" />
              <span><strong>Red dot</strong> at year 17 · <em>Trough.</em> Down {DRAWDOWN_PCT.toFixed(1)}% from peak — the number you must be able to stomach.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
              <span><strong>Green dot</strong> at year 20 · <em>Final value.</em> Recovery + compounding leaves you at {fmt$(FINAL.value)}.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-muted-foreground mt-1.5 shrink-0" />
              <span><strong>Dashed line</strong> at $10k · your original principal. Distance from the curve = total gain.</span>
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

function Callout({ n, icon: Icon, title, value, desc, tone }) {
  const toneMap = {
    primary: "border-primary/40 bg-primary/[0.04] text-primary",
    secondary: "border-secondary/40 bg-secondary/[0.06] text-secondary",
    warn: "border-destructive/40 bg-destructive/[0.05] text-destructive",
  };
  const border = toneMap[tone] || "border-border";
  return (
    <div className={`rounded-md border ${border} p-3`}>
      <div className="flex items-center gap-1.5">
        <span className="mono text-[9px] uppercase tracking-widest opacity-70">{n}</span>
        <Icon className="w-3.5 h-3.5" />
        <span className="mono text-[10px] uppercase tracking-widest">{title}</span>
      </div>
      <div className="serif text-2xl mt-1">{value}</div>
      <div className="text-[11px] text-foreground/70 mt-1 leading-snug">{desc}</div>
    </div>
  );
}
